import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaSave, FaTimes, FaThList, FaTh, FaSearch, FaCog, FaArchive, FaUpload } from 'react-icons/fa';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import DataTable from 'react-data-table-component-with-filter';
import InternalHeader from '../InternalHeader';
import Sidebar from '../Sidebar';
import '../Treatments/Treatment.css';
import CustomPagination from '../CustomPagination/CustomPagination';
import FilterInventory from "../Filter/FilterInventory";
import Actions from "../Actions/Actions";
import CreateInventory from './CreateInventory'; // Import the CreateInventory component
import Papa from 'papaparse'; // Import PapaParse for CSV parsing
import { format } from 'date-fns';
import AccessControl from '../AccessControl';

const Inventory = () => {
    const navigate = useNavigate();
    const [filterText, setFilterText] = useState('');
    const [items, setItems] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [editedItem, setEditedItem] = useState({
        Name: "",
        Quantity: "",
        Unit: "",
        Price: "",
        Store: "",
        Picture: "",
        CriticalStock: "",
    });
    const [showModal, setShowModal] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const totalRecords = items.length;
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewOption, setViewOption] = useState('all');
    const [selectedInventory, setSelectedInventory] = useState([]);
    const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
    const [stores, setStores] = useState([]);
    const [categories, setCategories] = useState([]);
    const [logs, setLogs] = useState([]);

    const logChange = (action, details) => {
        setLogs(prevLogs => [...prevLogs, { timestamp: new Date().toLocaleString(), action, details }]);
    };

    useEffect(() => {
        const fetchStoresAndCategories = async () => {
            try {
                const storesResponse = await fetch('https://api.hgcdental.clinic/api/stores');
                const storesData = await storesResponse.json();
                setStores(storesData);

                const categoriesResponse = await fetch('https://api.hgcdental.clinic/api/categories');
                const categoriesData = await categoriesResponse.json();
                setCategories(categoriesData);
            } catch (error) {
                console.error('Error fetching stores or categories:', error);
            }
        };

        fetchStoresAndCategories();
    }, []);

    useEffect(() => {
        const fetchInventory = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/inventory');
                const data = await response.json();

                // Map store and category details to each inventory item
                const mappedData = data.map(item => {
                    const store = stores.find(store => store._id === item.Store);
                    const category = categories.find(category => category._id === item.Category);

                    return {
                        ...item,
                        Store: store ? store.StoreContact : 'N/A',
                        Category: category ? category.Name : 'N/A',
                    };
                });

                setItems(mappedData);
            } catch (error) {
                console.error('Error fetching inventory:', error);
            }
        };

        fetchInventory();
    }, [stores, categories]); // Re-fetch inventory when stores or categories change

    useEffect(() => {
        const fetchInventory = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/inventory');
                const data = await response.json();
                setItems(data);
                logChange('Fetched', `Loaded ${data.length} inventory.`);
            } catch (error) {
                console.error('Error fetching inventory:', error);
            }
        };

        fetchInventory();
    }, []);

    // Handle Import Function
    const handleImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        Papa.parse(file, {
            complete: async (result) => {
                if (!result.data || result.data.length < 2) {
                    alert("Invalid or empty CSV file.");
                    return;
                }

                // Ensure the first row contains headers
                const headers = result.data[0];
                if (!Array.isArray(headers)) {
                    alert("CSV format error: Headers not recognized.");
                    return;
                }

                // Format CSV rows into objects
                const formattedData = result.data.slice(1).map(row => {
                    let item = {};
                    headers.forEach((header, index) => {
                        if (row[index] !== undefined) {
                            item[header.trim()] = row[index]?.trim();
                        }
                    });
                    return item;
                });

                try {
                    const response = await fetch('https://api.hgcdental.clinic/api/inventory/import', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(formattedData),
                    });

                    const result = await response.json();
                    if (!response.ok) throw new Error(result.message || 'Import failed');

                    alert(result.message);
                    setItems((prev) => [...prev, ...result.insertedItems]);
                } catch (error) {
                    console.error('Error importing:', error);
                    alert(error.message || 'Failed to import inventory items');
                }
            },
            header: false, // We will manually extract headers from the first row
            skipEmptyLines: true, // Ignore empty rows
        });
    };

    // Handle Export Function
    const handleExport = () => {
        if (!Array.isArray(selectedInventory) || selectedInventory.length === 0) {
            alert('No records selected for export!');
            return;
        }

        // Define headers for the CSV file
        const headers = [
            '_id', 'Name', 'Quantity', 'Unit', 'Price', 'Store', 'CriticalStock', 'createdAt', 'updatedAt'
        ];

        // Prepare CSV rows
        const csvRows = [
            headers, // Add header row
            ...selectedInventory.map(record =>
                headers.map(field => {
                    let value = record[field];

                    // Handle null or undefined values
                    if (value == null) return '';

                    // Explicit handling for boolean values
                    if (typeof value === 'boolean') {
                        return value ? 'true' : 'false';
                    }

                    // Escape values containing special characters (commas, quotes, etc.)
                    if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                        value = `"${value.replace(/"/g, '""')}"`; // Escape double quotes by doubling them
                    }

                    return value;
                })
            ),
        ];

        // Convert rows to CSV format
        const csvContent = csvRows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);

        // Trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = 'InventoryRecords.csv';
        link.click();
        URL.revokeObjectURL(url); // Clean up the URL object
    };

    // Handle Export Function
    const handleDownloadTemplate = () => {
        // Define headers for the CSV file
        const headers = [
            '_id', 'Name', 'Quantity', 'Unit', 'Price', 'Store', 'CriticalStock', 'createdAt', 'updatedAt'
        ];

        // Prepare CSV rows
        const csvRows = [
            headers, // Add header row
            ...selectedInventory.map(record =>
                headers.map(field => {
                    let value = record[field];

                    // Handle null or undefined values
                    if (value == null) return '';

                    // Explicit handling for boolean values
                    if (typeof value === 'boolean') {
                        return value ? 'true' : 'false';
                    }

                    // Escape values containing special characters (commas, quotes, etc.)
                    if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                        value = `"${value.replace(/"/g, '""')}"`; // Escape double quotes by doubling them
                    }

                    return value;
                })
            ),
        ];

        // Convert rows to CSV format
        const csvContent = csvRows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);

        // Trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = 'InventoryRecords.csv';
        link.click();
        URL.revokeObjectURL(url); // Clean up the URL object
    };

    const handleDeleteSelected = async () => {
        if (selectedInventory.length === 0) {
            alert('No inventory items selected for archiving!');
            return;
        }

        const confirmArchive = window.confirm('Are you sure you want to archive the selected items?');
        if (!confirmArchive) {
            return;
        }

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/inventory/archive', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    inventoryIds: selectedInventory.map(item => item._id)
                })
            });

            const result = await response.json();

            if (response.ok) {
                // Update the local state to reflect the archived status
                setItems(prevItems =>
                    prevItems.map(item =>
                        selectedInventory.some(selected => selected._id === item._id)
                            ? { ...item, isArchived: true }
                            : item
                    )
                );
                setSelectedInventory([]); // Clear selected inventory items
                alert(result.message);
            } else {
                alert(result.message || 'Failed to archive inventory items');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('An error occurred while archiving inventory items');
        }
    };

    const handleUnDeleteSelected = async () => {
        if (selectedInventory.length === 0) {
            alert('No inventory items selected for archiving!');
            return;
        }

        const confirmUnarchive = window.confirm('Are you sure you want to unarchive the selected items?');
        if (!confirmUnarchive) {
            return;
        }

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/inventory/unarchive', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    inventoryIds: selectedInventory.map(item => item._id)
                })
            });

            const result = await response.json();

            if (response.ok) {
                // Update the local state to reflect the archived status
                setItems(prevItems =>
                    prevItems.map(item =>
                        selectedInventory.some(selected => selected._id === item._id)
                            ? { ...item, isArchived: false }
                            : item
                    )
                );
                setSelectedInventory([]); // Clear selected inventory items
                alert(result.message);
            } else {
                alert(result.message || 'Failed to unarchive inventory items');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('An error occurred while unarchiving inventory items');
        }
    };

    const handleFilterChange = (event) => {
        setFilterText(event.target.value);
    };

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    const addItem = async (newItem) => {
        try {
            const response = await fetch('https://api.hgcdental.clinic/api/inventory', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newItem),
            });
            const data = await response.json();
            setItems(prevItems => [...prevItems, data]);
        } catch (error) {
            console.error('Error adding item:', error);
        }
    };

    const handleRowSelected = ({ selectedRows }) => {
        setSelectedInventory(selectedRows);
    };

    const handleDeleteClick = async (itemId) => {
        try {
            await fetch(`https://api.hgcdental.clinic/api/inventory/${itemId}`, {
                method: 'DELETE',
            });
            setItems(prevItems => prevItems.filter(item => item.id !== itemId));
        } catch (error) {
            console.error('Error deleting item:', error);
        }
    };

    const confirmDelete = () => {
        handleDeleteClick(itemToDelete.id);
        setShowDeleteConfirmation(false);
    };

    const cancelDelete = () => {
        setShowDeleteConfirmation(false);
        setItemToDelete(null);
    };

    const handleEditClick = (item) => {
        setSelectedItemId(item.id);
        setEditedItem({
            Name: item.Name,
            Quantity: item.Quantity,
            Unit: item.Unit,
            Price: item.Price,
            Store: item.Store,
            Picture: item.Picture,
            CriticalStock: item.CriticalStock,
        });
        setIsEditing(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedItem(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSaveClick = async () => {
        const formData = new FormData();
        formData.append('Name', editedItem.Name);
        formData.append('Quantity', editedItem.Quantity);
        formData.append('Unit', editedItem.Unit);
        formData.append('Price', editedItem.Price);
        formData.append('Store', editedItem.Store);
        formData.append('StoreContact', editedItem.StoreContact);
        formData.append('CriticalStock', editedItem.CriticalStock);
        if (editedItem.Picture) {
            formData.append('Picture', editedItem.Picture);
        }

        try {
            const response = await fetch(`https://api.hgcdental.clinic/api/inventory/${selectedItemId}`, {
                method: 'PUT',
                body: formData,
            });
            const data = await response.json();
            setItems(prevItems =>
                prevItems.map(item =>
                    item.id === selectedItemId ? data : item
                )
            );
            setIsEditing(false);
            setSelectedItemId(null);
        } catch (error) {
            console.error('Error updating item:', error);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setSelectedItemId(null);
        setEditedItem({ Name: "", Quantity: "", Price: "", Store: "", StoreContact: "", CriticalStock: "" });
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleActionsToggle = () => {
        setIsActionsDropdownOpen(!isActionsDropdownOpen);
    };

    const columns = [
        {
            name: 'Name',
            selector: row => row.Name || '',
            sortable: true,
            filterable: true,
            cell: row => isEditing && selectedItemId === row.id ? (
                <input
                    type="text"
                    name="Name"
                    value={editedItem.Name}
                    onChange={handleInputChange}
                />
            ) : (
                row.Name
            ),
        },
        {
            name: 'Quantity',
            selector: row => row.Quantity || '',
            sortable: true,
            filterable: true,
            cell: row => {
                const isLowStock = row.Quantity < row.CriticalStock;
                return (
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        color: isLowStock ? '#d32f2f' : 'inherit',
                        fontWeight: isLowStock ? 'bold' : 'normal'
                    }}>
                        {isLowStock && (
                            <span style={{ 
                                marginRight: '8px',
                                animation: 'pulse 1.5s infinite'
                            }}>
                                ⚠
                            </span>
                        )}
                        {isEditing && selectedItemId === row.id ? (
                            <input
                                type="number"
                                name="Quantity"
                                value={editedItem.Quantity}
                                onChange={handleInputChange}
                                style={{ borderColor: isLowStock ? '#ff4444' : '' }}
                            />
                        ) : (
                            row.Quantity
                        )}
                    </div>
                );
            },
        },
        {
            name: 'Unit',
            selector: row => row.Unit || '',
            sortable: true,
            filterable: true,
            cell: row => isEditing && selectedItemId === row.id ? (
                <select
                    name="Unit"
                    value={editedItem.Unit}
                    onChange={handleInputChange}
                >
                    <option value="">Select Unit</option>
                    <option value="kg">Kilogram (kg)</option>
                    <option value="g">Gram (g)</option>
                    <option value="L">Liter (L)</option>
                    <option value="mL">Milliliter (mL)</option>
                    <option value="mg">Milligrams (mg)</option>
                </select>
            ) : (
                row.Unit
            ),
        },
        {
            name: 'Price',
            selector: row => row.Price || '',
            sortable: true,
            filterable: true,
            cell: row => isEditing && selectedItemId === row.id ? (
                <input
                    type="number"
                    name="Price"
                    value={editedItem.Price}
                    onChange={handleInputChange}
                />
            ) : (
                // Format the price for display with the peso sign
                `₱${(row.Price || 0).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                })}`
            ),
        },
        {
            name: 'Category',
            selector: row => row.Category || '',
            sortable: true,
            filterable: true,
            cell: row => isEditing && selectedItemId === row.id ? (
                <input
                    type="text"
                    name="Category"
                    value={editedItem.Category}
                    onChange={handleInputChange}
                />
            ) : (
                row.Category
            ),
        },
        {
            name: 'Store',
            selector: row => row.Store || '',
            sortable: true,
            filterable: true,
            cell: row => isEditing && selectedItemId === row.id ? (
                <input
                    type="text"
                    name="Store"
                    value={editedItem.Store}
                    onChange={handleInputChange}
                />
            ) : (
                row.Store
            ),
        },
        // {
        //     name: 'Critical Stock',
        //     selector: row => row.CriticalStock || '',
        //     sortable: true,
        //     filterable: true,
        //     cell: row => isEditing && selectedItemId === row.id ? (
        //         <input
        //             type="number"
        //             name="CriticalStock"
        //             value={editedItem.CriticalStock}
        //             onChange={handleInputChange}
        //         />
        //     ) : (
        //         row.CriticalStock
        //     ),
        // },
        {
            name: 'Date Added',
            selector: row => new Date(row.createdAt),
            sortable: true,
            filterable: true,
            cell: row => format(new Date(row.createdAt), 'MM/dd/yyyy hh:mm a')
        },
        {
            name: 'Date Updated',
            selector: row => new Date(row.updatedAt),
            sortable: true,
            filterable: true,
            cell: row => format(new Date(row.updatedAt), 'MM/dd/yyyy hh:mm a')
        }
    ];

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear().toString().slice(-2);

        return `${month < 10 ? '0' + month : month}/${day < 10 ? '0' + day : day}/${year}`;
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(Number(newRowsPerPage));
        setCurrentPage(1);
    };

    const filteredItems = items.filter(item =>
        item.Name && item.Name.toLowerCase().includes(filterText.toLowerCase()) &&
        (viewOption === 'all' ? !item.isArchived : item.isArchived)
    );

    const paginatedData = filteredItems.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const totalPages = Math.ceil(filteredItems.length / rowsPerPage);

    const handleRowClick = (row) => {
        navigate(`/inventory-details/${row._id}`);
    };

    const customStyles = {
        rows: {
            style: (row) => ({
                borderBottom: '1px solid #ddd',
                backgroundColor: row.Quantity < row.CriticalStock ? '#fff8f8' : 'transparent',
                '&:hover': {
                    backgroundColor: row.Quantity < row.CriticalStock ? '#ffecec' : '#f5f5f5',
                },
            }),
        },
        headCells: {
            style: {
                backgroundColor: '#007bff',
                color: 'white',
                fontWeight: 'bold',
                padding: '15px 10px',
                textAlign: 'center',
            },
        },
        cells: {
            style: {
                padding: '12px 10px',
                borderRight: '1px solid #ddd',
            },
        },
        pagination: {
            style: {
                display: 'flex',
                justifyContent: 'center',
                padding: '10px',
                backgroundColor: '#f4f4f4',
            },
        },
        sortIcon: {
            style: {
                color: '#007bff',
                fontSize: '18px',
            },
        },
    };

    return (
        <AccessControl>
            <div className={`data-table-page ${isSidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'}`}>
                <InternalHeader moduleName="Inventory" toggleSidebar={toggleSidebar} />
                <div className="data-table-container">
                    <Sidebar isSidebarVisible={isSidebarVisible} />
                    <div className="data-table-container2">
                        <div className="data-table-header">
                            <div className="flex-div">
                                <a href="/create-inventory"><button className="create-button">New</button></a>
                                <Actions
                                    onImportClick={() => {
                                        document.getElementById('file-input').click();
                                    }}
                                    onDownloadTemplateClick={() => {
                                        handleDownloadTemplate();
                                    }}
                                />
                                <input
                                    id="file-input"
                                    type="file"
                                    accept=".csv"
                                    style={{ display: 'none' }}
                                    onChange={handleImport}
                                />
                            </div>
                            <div>
                                {/* Conditionally render Filter or Actions dropdown */}
                                {selectedInventory.length > 0 ? (
                                    <div className="actions-dropdown-container">
                                        <button onClick={handleActionsToggle} className="actions-button">
                                            <FaCog /> Actions
                                        </button>
                                        {isActionsDropdownOpen && (
                                            <div className="actions-dropdown-options1">
                                                <div onClick={handleExport}>
                                                    <FaUpload /> Export Records
                                                </div>
                                                {(viewOption === 'all' || viewOption === 'active') && (
                                                    <div onClick={handleDeleteSelected}>
                                                        <FaArchive /> Archive
                                                    </div>
                                                )}
                                                {/* Conditionally render the Unarchive button */}
                                                {viewOption === 'archived' && (
                                                    <div onClick={handleUnDeleteSelected}>
                                                        <FaArchive /> Unarchive
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <FilterInventory
                                        searchText={filterText}
                                        setSearchText={setFilterText}
                                        viewOption={viewOption}
                                        setViewOption={setViewOption}
                                    />
                                )}
                            </div>
                            <div className="column-div">
                                <CustomPagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    rowsPerPage={rowsPerPage}
                                    onPageChange={handlePageChange}
                                    onRowsPerPageChange={handleRowsPerPageChange}
                                    totalRecords={filteredItems.length}
                                />
                            </div>
                        </div>
                        <div className="table-form">
                            <div>
                                <DataTable
                                    title="Inventory"
                                    columns={columns}
                                    data={paginatedData}
                                    highlightOnHover
                                    selectableRows
                                    customStyles={customStyles}
                                    striped
                                    onRowClicked={handleRowClick} // Add this line
                                    onSelectedRowsChange={handleRowSelected}
                                />
                            </div>
                        </div>
                        <div className="log-display-section">
                            <h2>Logs</h2>
                            <div className="log-container">
                                <ul>
                                    {logs.map((log, index) => (
                                        <li key={index}>{log.timestamp} - {log.action}: {log.details}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                {showModal && (
                    <CreateInventory
                        onClose={() => setShowModal(false)}
                        onAddItem={(newItem) => setItems(prevItems => [...prevItems, newItem])}
                    />
                )}
            </div>
        </AccessControl>
    );
};

export default Inventory;